import type { OnboardFormValues, OnboardIntent, PublicOnboardRequestDto, TezamPlanCode } from "@/shared/types/onboard.types";
import { previewTenantSlug } from "@/shared/utils/tenantDomainPreview";
import { getPartnerRefCookie } from "@/shared/utils/partnerRef";
import { readStoredUtm } from "@/shared/utils/utm";

/** Landing aliases → canonical platform plan codes (backend also aliases starter→demo). */
const BACKEND_PLAN_CODE: Partial<Record<TezamPlanCode, TezamPlanCode>> = {
    starter_services: "demo",
    starter_goods: "demo",
    starter_combo: "demo",
    custom_config: "demo",
    starter: "demo",
    max_full: "business",
    demo: "demo",
    start: "start",
    business: "business",
    pro: "pro",
    pro_alfa: "pro_alfa",
    holding: "holding",
    enterprise: "holding",
};

/** Partner promo ALFA → Pro Alfa offer (30d trial, 50 BYN/mo). Backend also forces via offer registry. */
export const ALFA_PARTNER_PROMO = "ALFA";
export const ALFA_OFFER_PLAN: TezamPlanCode = "pro_alfa";

export function isAlfaPartnerPromo(code?: string | null): boolean {
    return Boolean(code?.trim() && code.trim().toUpperCase() === ALFA_PARTNER_PROMO);
}

function getOnboardSource(): string {
    return process.env.NEXT_PUBLIC_ENV === "prod" ? "landing-tezam.by" : "landing-dev.tezam.by";
}

function getHostingKind(): "prod-shared" | "dev-shared" {
    return process.env.NEXT_PUBLIC_ENV === "prod" ? "prod-shared" : "dev-shared";
}

export function resolveBackendPlanCode(planCode: TezamPlanCode): TezamPlanCode {
    return BACKEND_PLAN_CODE[planCode] ?? planCode;
}

export function buildPublicOnboardPayload(
    values: OnboardFormValues,
    onboard: OnboardIntent,
    pagePath?: string,
    leadSource?: string,
): PublicOnboardRequestDto {
    const preferredSlug = previewTenantSlug(values.companyName);
    const refSlug = getPartnerRefCookie();
    const promoTrimmed = values.promoCode?.trim();
    const alfaOffer = isAlfaPartnerPromo(promoTrimmed);
    const utm = readStoredUtm();
    const campaignMeta =
        onboard.metadata && "campaign" in onboard.metadata ? String(onboard.metadata.campaign) : undefined;

    const resolvedPlan: TezamPlanCode = alfaOffer
        ? ALFA_OFFER_PLAN
        : onboard.planCode
          ? resolveBackendPlanCode(onboard.planCode)
          : "demo";

    return {
        planCode: resolvedPlan,
        companyName: values.companyName.trim(),
        contactEmail: values.contactEmail.trim(),
        contactPhone: values.contactPhone.trim() || undefined,
        contactName: values.contactName.trim() || undefined,
        preferredSlug: preferredSlug || undefined,
        industryPreset: values.industryPreset ? values.industryPreset : undefined,
        locale: "ru",
        source: getOnboardSource(),
        metadata: {
            ...(onboard.metadata ?? {}),
            pagePath,
            flow: onboard.mode,
            hostingKind: getHostingKind(),
            ...(leadSource ? { lead_source: leadSource } : {}),
            ...(campaignMeta ? { campaign: campaignMeta } : {}),
            ...utm,
            ...(promoTrimmed ? { promoCode: promoTrimmed.toUpperCase() } : {}),
            // Promo wins over cookie for attribution; +7 trial only when refSlug is sent (no promo).
            ...(!promoTrimmed && refSlug ? { refSlug, trialDays: 21 } : {}),
            ...(alfaOffer ? { partnerOffer: "ALFA", trialDays: 30 } : {}),
        },
    };
}

export function isOnboardFlow(onboard?: OnboardIntent): boolean {
    return onboard?.mode === "onboard" && Boolean(onboard.planCode);
}

export function isConsultationFlow(onboard?: OnboardIntent): boolean {
    return onboard?.mode === "consultation";
}
